import { useState, useEffect } from 'react';
import { useAppDispatch, useAppSelector } from 'src/hooks';
import Storage from 'src/utils/storage/storage';
import {
  Box,
  LinearProgress,
  Grid,
  ToggleButtonGroup,
  ToggleButton,
  Switch,
  CircularProgress
} from '@mui/material';
import './simulation-summary.scss';
import {
  flattenColumnsData,
  flattenColumnsHeader,
  getDataForSimulatedRows
} from 'src/components/collapsible-table/table-data-prep';
import useModuleMetadata from 'src/hooks/use-module-metadata.hook';
import FileDownloadIcon from '@mui/icons-material/FileDownload';
import useMatomoFunction from 'src/hooks/use-matomo-function.hook';
import CustomDownloadButton from 'src/components/buttons/button-with-spinner-v2';
import {
  downloadSimulationSummaryData,
  getMultiyearEstimateData,
  handleMultiyearSimulationFlagValue,
  resetMultiyearEstimate,
  saveMultiyearSimulationData,
  savePotentialRisksUpsidesData,
  getCurrencyToggledata,
  toggleCurrency,
  handleCurrencyToggleState
} from './store/simulation-summary-slice';
import PotentialRiskTableContainerComponent from './components/potential-risk-table/potential-risk-table-container';
import SimulatorFooter from '../components/simulation-footer/simulator-footer';
import MultiyearEstimateComponent from './components/multiyear-estimate/multiyear-estimate';
import MODULE_TO_PAGE_MAP from '../home/types/simulation-module-to-page-map';
import useModulePreRequisites from '../common/hooks/use-module-pre-requisites.hook';
import AsIsToBeGrid from './components/as-is-to-be-v2/as-is-to-be-v2';
import SaveAsContainer from '../components/save-as-container/save-as-container';
import Summary from './components/summary/summary';
import { ModuleSaveButton } from '../common';
import {
  handleInputLevelToggleChange,
  setTableMetaData
} from '../common/features/table/store/table-slice';
import { MatomoEventTrackMapObject } from '../constants/constant';
import { getSegmentDetails } from '../../landing/store/landing-slice';
import { fetchCurrencyToggledata } from './api/simulation-summary-api';

export default function SimulationSummary() {
  const appDispatch = useAppDispatch();
  const { moduleId, sourceId, scenarioId } = useModuleMetadata({
    module: MODULE_TO_PAGE_MAP.SUMMARY
  });

  const { fetchingMetaData, multiyearEstimateData, currencyToggle, isTargetEnabled } = useAppSelector(
    (state) => state.simulationSummary
  );

  // fetching summary tab details (Available in segmentDetailsData value)
  const segmentDetailsSrorage = Storage.getSegmentDetails();
  // fetching summary tab details (Available in segmentDetailsData value)
  const { segmentDetailsData } = useAppSelector((state) => state.landing);
  // getting summary dynamic access
  //const currency = segmentDetailsData.currency

  const summaryDynamiceAccess = segmentDetailsData.summary_dynamic_access
    ? segmentDetailsData.summary_dynamic_access
    : segmentDetailsSrorage.summary_dynamic_access;

  const { callFunction } = useMatomoFunction();
  const getPageName = () => {
    const pageTitle = window.location.href.split('/').at(-1);
    return pageTitle;
  };

  useEffect(() => {
    appDispatch(getCurrencyToggledata({
      sourceId,
      scenarioId
    }));
  }, [sourceId, scenarioId]);

  const local_currency = currencyToggle?.find((item) => item.order === 1);
  const target_currency = currencyToggle?.find((item) => item.order === 2);
  const isSameCurrency = local_currency?.alias === target_currency?.alias || !target_currency; 

  useEffect(() => {
    callFunction(
      MatomoEventTrackMapObject.track_page_view.event_key,
      getPageName()
    );
  }, []);

  useEffect(() => {
    fetchCurrencyToggledata(sourceId, scenarioId)
      .then((res) => {
        if (res?.is_target_enabled !== undefined) {
          // Dispatch the state to Redux
          appDispatch(handleCurrencyToggleState(res.is_target_enabled));
        }
      })
      .catch((err) => console.error('Failed to fetch currency state:', err));
  }, [appDispatch, sourceId, scenarioId]);

  useEffect(() => {
    appDispatch(setTableMetaData({ tableData: multiyearEstimateData }));
  }, [multiyearEstimateData]);
  // useEffect(()=>{
  //   appDispatch(getSegmentDetails(sourceId));
  // },[sourceId])
  const [selectedTab, setSelectedTab] = useState('summary');
  const handleTabChange = (event, newTab) => {
    setSelectedTab(newTab);
    window.location.hash = newTab; // Update the URL hash with the new tab
  };
  const { summary: summaryAccessConfig } = useAppSelector(
    (state) => state.simulatorHome.moduleAccessMap
  );
  const { tableMetadaData, outputToggle } = useAppSelector(
    (state) => state.table
  );

  const [isDownloading, setIsDownloading] = useState(false);

  const visibleSummaryDynamicAccess = summaryDynamiceAccess.length ? summaryDynamiceAccess.filter(
    (item) => item.is_visible === true
  ) : [];

  const toggleData: { alias: string; key: string; disable: boolean }[] =
    visibleSummaryDynamicAccess;

  const { isFetchingPreRequisites } = useModulePreRequisites({
    module: MODULE_TO_PAGE_MAP.SUMMARY,
    onPreRequisitesFetched() {
      console.log('Pre requisites fetched');
    }
  });

  const { currentScenario } = useAppSelector((state) => state.simulatorHome);

  const onToggleBtnSwitch = (e) => {
    setSelectedTab(e.target.value);
    callFunction(
      MatomoEventTrackMapObject.track_tab_switch.event_key,
      e.target.value
    );
  };

  const getChangedInputRows = () => {
    const simulatedData = getDataForSimulatedRows(tableMetadaData);
    if (!simulatedData.inputs.length && !simulatedData.data.length) {
      return {
        isChanged: false,
        row: null
      };
    }
    return {
      isChanged: true,
      row: simulatedData
    };
  };

  const getSimulatedResult = () => {
    const flattenArray = flattenColumnsHeader(tableMetadaData);
    const simulatedPayload = {
      data: [],
      inputs: []
    };
    const flattenData = flattenColumnsData(tableMetadaData.row).filter(
      (data) => data
    );
    const filteredHeader = flattenArray
      .filter(
        (headerData) => headerData && headerData.parent.is_part_of_payload
      )
      .map((headerOpt) => headerOpt.parent.parent_field);
    const childFieldMetadata = flattenArray.filter(
      (headerData) => headerData && headerData.parent.child_field
    );
    flattenData.forEach((row) => {
      let rowData = {};
      const parentDataVal = [];
      Object.keys(row).forEach((element) => {
        if (filteredHeader.includes(element)) {
          rowData = Object.assign(rowData, { [element]: row[element] });
        }
      });
      childFieldMetadata.forEach((field) => {
        if (
          row[field.parent.child_field] !== 'NAN' &&
          row[field.parent.parent_field] !== 'NAN'
        ) {
          parentDataVal.push(field);
        }
      });
      if (childFieldMetadata.length === parentDataVal.length) {
        simulatedPayload.data.push(rowData);
      } else {
        simulatedPayload.inputs.push(rowData);
      }
    });
    appDispatch(handleMultiyearSimulationFlagValue());
    return {
      filter_inputs: [],
      row: simulatedPayload
    };
  };

  /**
   * Handler for simulate button click
   */
  const handleSimulationClick = () => {
    callFunction(
      MatomoEventTrackMapObject.track_simulate_button_click.event_key,
      getPageName()
    );
    const result = getChangedInputRows();
    if (!result.isChanged) {
      return;
    }
    const dataPoints = getSimulatedResult();
    appDispatch(
      getMultiyearEstimateData({
        dataInput: dataPoints,
        sourceId,
        moduleId,
        scenarioId
      })
    ).then((res) =>
      setTimeout(() => {
        appDispatch(
          handleInputLevelToggleChange({ toggledView: outputToggle })
        );
      }, 300)
    );
  };

  /**
   * Handler for simulation reset
   * Simply set the simulation id to null and refetch all the data again
   */
  const handleSimulationReset = () => {
    const resetMultiyear = {
      filter_inputs: {},
      row: {
        data: [],
        inputs: []
      }
    };
    appDispatch(
      resetMultiyearEstimate({
        dataInput: resetMultiyear,
        sourceId,
        moduleId,
        scenarioId
      })
    ).then((res) =>
      setTimeout(() => {
        appDispatch(
          handleInputLevelToggleChange({ toggledView: outputToggle })
        );
      }, 300)
    );
  };

  const handleSaveClick = (tab) => {
    if (tab === 'potentialRisks') {
      return appDispatch(
        savePotentialRisksUpsidesData({
          sourceId,
          moduleId,
          scenarioId
        })
      );
    }
    return appDispatch(
      saveMultiyearSimulationData({
        sourceId,
        moduleId,
        scenarioId
      })
    );
  };

  useEffect(() => {
    // Get the hash from the URL and set the active tab on page load
    const hash = window.location.hash.substring(1);
    if (hash) {
      setSelectedTab(hash);
    } else {
      setSelectedTab('summary');
    }
  }, []);

  const toggleCurrencySwitch = (e) => {
    appDispatch(handleCurrencyToggleState(e.target.checked));
    appDispatch(toggleCurrency({
      sourceId,
      scenarioId,
      isTargetEnabled: e.target.checked,
    }));
    const currentTab = selectedTab;
    window.location.hash = currentTab; // Update the URL with the current tab

    // Reload the page
    setTimeout(() => {
      window.location.reload();
    }, 2000);
  };

  const handleDownloadClick = () => {
    callFunction(
      MatomoEventTrackMapObject.track_download_button_click.event_key,
      getPageName()
    );
    setIsDownloading(true);
    return appDispatch(
      downloadSimulationSummaryData({ sourceId, moduleId, scenarioId })
    )
      .then((response) => {
        console.log(response);
      })
      .catch((error) => {
        console.error('Failed to download summary', error);
      })
      .finally(() => {
        setIsDownloading(false);
      });
  };

  return fetchingMetaData || isFetchingPreRequisites ? (
    <div className='parent-loader-div'>
      <LinearProgress className='loader-div' />{' '}
    </div>
  ) : (
    <>
      <Box>
        <Grid sx={{ padding: (theme) => theme.spacing(1, 2) }}>
          <Grid
            container
            spacing={2}
            sx={{ marginBottom: '10px' }}
            alignItems='center'
          >
            <Grid sx={{ marginTop: '10px' }} xs={6} alignSelf='center' item>
              {toggleData ? (
                <ToggleButtonGroup
                  size='small'
                  color='primary'
                  value={selectedTab}
                  exclusive
                  onChange={(_event) => {
                    onToggleBtnSwitch(_event);
                  }}
                >
                  {toggleData.map((toggleInfo) => (
                    <ToggleButton
                      value={toggleInfo.key}
                      disabled={toggleInfo.disable}
                    >
                      {toggleInfo.alias}
                    </ToggleButton>
                  ))}
                </ToggleButtonGroup>
              ) : (
                <p style={{ color: 'red' }}>Tab data not available</p>
              )}
            </Grid>
            <Grid item xs={6} textAlign='right'>
              {!isSameCurrency && (<div style={{ display: 'inline-flex', alignItems: 'center', transform: 'translateX(-9px)' }}>
                <span className={`currency-label ${!isTargetEnabled ? 'active' : 'non-active'}`} >
                  {local_currency?.alias}

                </span>
                <Switch
                  onChange={(e) => toggleCurrencySwitch(e)}
                  checked={isTargetEnabled}
                  size='small'
                />
                <span className={`currency-label ${isTargetEnabled ? 'active' : 'non-active'}`} >
                  {target_currency?.alias}
                </span>
              </div>)}

              {selectedTab === 'summary' ? (
                <SaveAsContainer disabled={!summaryAccessConfig?.save_as} />
              ) : null}

              {selectedTab === 'multiyearEstimation' ||
                selectedTab === 'potentialRisks' ? (
                <ModuleSaveButton
                  disabled={!summaryAccessConfig?.save}
                  onSave={() => handleSaveClick(selectedTab)}
                />
              ) : null}
              <CustomDownloadButton
                styled={{ marginLeft: '15px' }}
                disabled={isDownloading}
                onClick={handleDownloadClick}
                buttonText='Download'
              />
            </Grid>
          </Grid>
          {selectedTab === 'summary' ? (
            <Summary
              scenarioId={scenarioId}
              moduleId={moduleId}
              sourceId={sourceId}
              fetchDataWithEmptyFilters={false}
              mode='summary'
            />
          ) : null}
          {selectedTab === 'potentialRisks' ? (
            <PotentialRiskTableContainerComponent
              sourceId={sourceId}
              moduleId={moduleId}
              scenarioId={scenarioId}
              mode='summary'
            />
          ) : null}
          {selectedTab === 'multiyearEstimation' ? (
            <MultiyearEstimateComponent
              sourceId={sourceId}
              moduleId={moduleId}
              scenarioId={scenarioId}
              inputsDisabled={false}
              mode='summary'
            />
          ) : null}
          {selectedTab === 'asIsVsToBe' ? (
            <AsIsToBeGrid
              scenarioName={currentScenario?.name}
              sourceId={sourceId}
              moduleId={moduleId}
              scenarioId={scenarioId}
              mode='summary'
            />
          ) : null}
        </Grid>
      </Box>
      <SimulatorFooter
        simulationClickHandler={handleSimulationClick}
        resetSimulationHandler={handleSimulationReset}
        isSimulationDisabled={
          selectedTab !== 'multiyearEstimation' ||
          !summaryAccessConfig?.simulate_multi_year_estimate
        }
        nextClickHandler={() => { }}
        isButtonSectionVisible={selectedTab === 'multiyearEstimation'}
        isNextButtonHidden
        isResetSimulationDisabled={
          !summaryAccessConfig?.reset_multi_year_estimate
        }
      />
    </>
  );
}
